// Visual-labs helpers: classify each analyte into an icon family, parse its
// reference range, decide low/normal/high, and lay out a 270° radial gauge.

export type LabStatus = 'low' | 'normal' | 'high' | 'na';
export type Range = { min: number; max: number };

export function numeric(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') { const n = parseFloat(v.replace(/[^0-9.\-]/g, '')); return Number.isFinite(n) ? n : null; }
  return null;
}

// "3.50-9.92" | "11.9-17.0" | "<0.5" | ">40" | "10–562" → {min,max}
export function parseRange(s: string): Range | null {
  if (!s) return null;
  const t = s.replace(/[–—]/g, '-').replace(/\s/g, '');
  let m: RegExpMatchArray | null;
  if ((m = t.match(/^(-?\d*\.?\d+)-(-?\d*\.?\d+)$/))) return { min: +m[1], max: +m[2] };
  if ((m = t.match(/^[<≤](\d*\.?\d+)$/))) return { min: 0, max: +m[1] };
  if ((m = t.match(/^[>≥](\d*\.?\d+)$/))) return { min: +m[1], max: +m[1] * 1.5 };
  return null;
}

export function statusOf(value: number | null, range: Range | null, flag?: string): LabStatus {
  const f = (flag || '').toUpperCase();
  if (f === 'H') return 'high';
  if (f === 'L') return 'low';
  if (value == null || !range) return 'na';
  if (value < range.min) return 'low';
  if (value > range.max) return 'high';
  return 'normal';
}

// Fractions (0..1) of value/min/max along a display range that pads the normal
// band by 60% on each side, so low/high excursions read clearly on the arc.
export function gaugeFracs(value: number, range: Range) {
  const span = Math.max(range.max - range.min, 1e-6);
  const dMin = range.min - span * 0.6;
  const dMax = range.max + span * 0.6;
  const f = (x: number) => Math.min(1, Math.max(0, (x - dMin) / (dMax - dMin)));
  return { value: f(value), min: f(range.min), max: f(range.max) };
}

// ── Arc geometry: 270° gauge with the gap at the bottom (135° → 405°) ──────────
const START = 135, SWEEP = 270, C = 50;
const pt = (r: number, deg: number) => { const a = (deg * Math.PI) / 180; return [C + r * Math.cos(a), C + r * Math.sin(a)]; };
export function arcPath(r: number, f0: number, f1: number): string {
  const [x0, y0] = pt(r, START + SWEEP * f0);
  const [x1, y1] = pt(r, START + SWEEP * f1);
  const large = SWEEP * (f1 - f0) > 180 ? 1 : 0;
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}
export function pointAt(r: number, f: number): { x: number; y: number } {
  const [x, y] = pt(r, START + SWEEP * f);
  return { x, y };
}

// ── Clinical grouping: regroup all resulted analytes into standard panels ──────
export const LAB_GROUPS: { name: string; match: RegExp }[] = [
  { name: 'CBC + Differential', match: /wbc|white blood|rbc|red blood|h[ae]?moglob|\bhgb\b|\bhb\b|h[ae]matocrit|\bhct\b|\bmcv\b|\bmch\b|\bmchc\b|\brdw|platelet|\bmpv\b|\bplt\b|neutro|lymph|mono|eosin|baso|granul|immature|nrbc|slide review/i },
  { name: 'Chemistry 10 (Metabolic)', match: /sodium|potassium|chloride|\bco2\b|anion|\bbun\b|creatin|glucose|calcium|magnes|phosph|egfr/i },
  { name: 'Inflammatory Markers', match: /crp|c-reactive|\besr\b|sed rate|procalciton|ferritin/i },
  { name: 'Liver Function', match: /\bast\b|sgot|\balt\b|sgpt|alk.*phos|\balp\b|\bggt\b|bilirub|albumin|globulin|protein|\bldh\b/i },
  { name: 'Coagulation', match: /\binr\b|prothrombin|\bptt\b|\bpt\b|fibrinogen|d-dimer|thrombin/i },
  { name: 'Immunology', match: /ig[gmae]\b|antibod|serotype|quantiferon|\bhiv\b|mrsa|fungal|karius|diphther|tetanus|brucella|\bpmn\b|stimulation/i },
];

export type LabGroup = { name: string; source?: string; rows: any[] };

// Flatten every resulted analyte across panels, dedupe by name, and bin into the
// groups above (first match wins). Everything left lands in "Comprehensive List".
export function groupLabs(panels: any[]): LabGroup[] {
  const seen = new Set<string>();
  const flat: any[] = [];
  for (const p of panels || []) for (const r of p.results || []) {
    if (r.value == null || String(r.value).trim() === '') continue;
    const key = (r.analyte || '').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    flat.push({ ...r, _source: p.source });
  }
  const taken = new Set<string>();
  const groups: LabGroup[] = [];
  for (const g of LAB_GROUPS) {
    const rows = flat.filter((r) => !taken.has(r.analyte) && g.match.test(r.analyte));
    rows.forEach((r) => taken.add(r.analyte));
    if (rows.length) groups.push({ name: g.name, source: rows[0]._source, rows });
  }
  const rest = flat.filter((r) => !taken.has(r.analyte));
  if (rest.length) groups.push({ name: 'Comprehensive List (Additional)', source: rest[0]._source, rows: rest });
  return groups;
}

// Analyte → icon family (LabIcon handles each key).
export function categorize(analyte: string): string {
  const t = (analyte || '').toLowerCase();
  if (/rbc|red blood|nrbc/.test(t)) return 'rbc';
  if (/mcv|mchc|\bmch\b|\brdw\b/.test(t)) return 'index';
  if (/h[ae]?moglob|\bhgb\b|\bhb\b/.test(t)) return 'hgb';
  if (/h[ae]matocrit|\bhct\b/.test(t)) return 'hct';
  if (/platelet|\bmpv\b|\bplt\b/.test(t)) return 'platelet';
  if (/wbc|white blood|neutro|lymph|mono|eosin|baso|granul|immature|slide review/.test(t)) return 'wbc';
  if (/crp|c-reactive|\besr\b|sed rate|procalciton|ferritin/.test(t)) return 'inflammation';
  if (/creatin|\bbun\b|egfr|uric|ckid/.test(t)) return 'kidney';
  if (/ast|alt|alk.*phos|\balp\b|ggt|bilirub|albumin|globulin|protein|\bldh\b/.test(t)) return 'liver';
  if (/glucose/.test(t)) return 'glucose';
  if (/sodium|potassium|chloride|\bco2\b|anion|calcium|magnes|phosph|lactate|\bph\b|pco2|po2|base excess/.test(t)) return 'electrolyte';
  if (/ig[gmae]\b|antibod|serotype|quantiferon|hiv|mrsa|fungal|karius|diphther|tetanus|brucella/.test(t)) return 'antibody';
  if (/inr|prothrombin|\bptt\b|\bpt\b|fibrinogen|d-dimer|thrombin/.test(t)) return 'coag';
  if (/vancomycin|trough|\blevel/.test(t)) return 'drug';
  return 'vial';
}
